/**
 * Referenz-Angebote (Migration 153, docs/KONZEPT-REFERENZ-ANGEBOTE.md §5.10): Upload, Liste,
 * Original öffnen, Archivieren, Analyse-Status und Stilprofil-Karte.
 *
 * Wissen-Umbau (Antoines Go 05.08.2026): Die Fläche lebt jetzt unter Wissen → „Woraus MAESTRO
 * lernt" (knowledge.tsx bindet `<ReferenceDocsSection />` ein) — vorher als Bereich unter den
 * Speisekarten-Kacheln im Katalog; dort steht heute nur noch ein Verweis. Der Ort im Produkttext
 * heißt REFERENZ_UPLOAD_ORT (apps/api/src/lib/betriebsreife.ts) und muss mit dieser Einbindung
 * übereinstimmen — der Agent nennt Betreibern diesen Pfad.
 */
import { useEffect, useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { openAuthenticatedFile, fileToBase64 } from "../lib/files";
import { Icon, ErrorNote, StatusPill, Button, Input, Card, Select, useBestaetigung, Textarea } from "../ui/components";
import type { StatusMeta } from "../ui/format";
import type { ReferenceDoc, StyleProfile, StyleProfileRow } from "../ui/types";
import { useReferenceProgress } from "../ui/ReferenceProgressProvider";

type Pillar = "catering" | "event";

// Spiegelt MAX_FILES in apps/api/src/routes/reference-docs.ts (Konzept §5.2).
const MAX_FILES = 6;

// Zweiter Eingang NEBEN dem Einzeldokument: ein Bestands-Export als Tabelle. Muss zu
// TABLE_MEDIA_TYPES in apps/api/src/routes/reference-docs.ts passen. Browser melden CSV je
// nach System als text/csv, application/csv oder text/plain - alle drei zulassen.
const TABLE_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/csv",
  "text/plain",
];

const PILLAR_META: Record<Pillar, { label: string; hint: string; icon: string }> = {
  catering: { label: "Catering (außer Haus)", hint: "Beispiel-Angebote für Vorgänge beim Kunden", icon: "delivery_dining" },
  event: { label: "Event (im Haus)", hint: "Beispiel-Angebote für Vorgänge im eigenen Haus", icon: "storefront" },
};

// Ton-Paare wie ui/format.ts PILL_CLASSES (Claude-Design-Entwurf, kein generisches Tailwind).
const STATUS_META: Record<ReferenceDoc["status"], StatusMeta> = {
  uploaded: { label: "Hochgeladen", tone: "info" },
  extracting: { label: "Wird analysiert …", tone: "warn" },
  extracted: { label: "Analysiert", tone: "success" },
  failed: { label: "Fehlgeschlagen", tone: "danger" },
  archived: { label: "Archiviert", tone: "neutral" },
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function mediaIcon(mediaType: string): string {
  if (mediaType === "application/pdf") return "picture_as_pdf";
  if (TABLE_TYPES.includes(mediaType)) return "table_chart";
  return "image";
}

/**
 * Dateiname -> lesbarer Titel: Endung weg, Trennzeichen zu Leerzeichen, erster Buchstabe gross.
 * Bewusst simpel - es ist eine Vorbelegung, keine Interpretation. Das Feld bleibt editierbar.
 */
function titleFromFileName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s{2,}/g, " ").trim();
  if (!base) return name;
  const clipped = base.slice(0, 200);
  return clipped.charAt(0).toUpperCase() + clipped.slice(1);
}

/**
 * Wurde dieser Entwurf von Hand bearbeitet? „Neu erstellen" legt serverseitig eine NEUE Zeile an
 * (createdAt == updatedAt), eine gespeicherte Korrektur aktualisiert die bestehende. Ein Vorsprung
 * von updatedAt ist damit das Signal - ohne zusaetzliches Feld und ohne Migration.
 * Eine Sekunde Toleranz gegen Zeitstempel-Rauschen zwischen Insert und Rueckgabe.
 */
function wasHandEdited(row: StyleProfileRow): boolean {
  const created = Date.parse(row.createdAt);
  const updated = Date.parse(row.updatedAt);
  if (Number.isNaN(created) || Number.isNaN(updated)) return false;
  return updated - created > 1_000;
}

/**
 * Wie viele Angebote das Dokument beigesteuert hat. Ein Bestands-Export liefert viele, ein
 * einzelnes Angebot genau eines. Stand im Dokument mehr, als analysiert wurden, wird beides
 * genannt ("50 von 124 Angeboten") - eine Kappung soll sichtbar sein, nicht still passieren.
 */
function offerLabel(doc: ReferenceDoc): string | null {
  const count = doc.offerCount;
  if (count == null) return null;
  const found = doc.offersFound;
  if (found != null && found > count) return `${count} von ${found} Angeboten`;
  return `${count} Angebot${count === 1 ? "" : "e"}`;
}

export function ReferenceDocsSection() {
  const [open, setOpen] = useState<Pillar | null>(null);
  const docsQ = useApi<ReferenceDoc[]>("/api/reference-docs");
  const docs = docsQ.data ?? [];
  const countFor = (p: Pillar) => docs.filter((d) => d.pillar === p).length;

  /**
   * Solange ein Dokument analysiert wird, den Stand regelmaessig nachholen.
   *
   * Vorher hing die Anzeige an der Antwort des Analyse-Aufrufs: kommt die nicht an (abgebrochene
   * Verbindung, geschlossener Tab, Neuladen der Seite), blieb "Wird analysiert …" fuer immer
   * stehen - auch wenn der Server das Dokument laengst als fertig oder fehlgeschlagen gebucht
   * hatte. Der Betreiber sah einen Zustand, den es nicht mehr gab. Jetzt entscheidet der Server.
   */
  const isAnalysing = docs.some((d) => d.status === "extracting");
  const { reload } = docsQ;
  useEffect(() => {
    if (!isAnalysing) return;
    const timer = setInterval(reload, 5_000);
    return () => clearInterval(timer);
  }, [isAnalysing, reload]);

  return (
    <div className="mt-8">
      <div className="mb-3">
        <h2 className="font-title-md text-title-md font-semibold text-on-background dark:text-inverse-on-surface">Referenzen</h2>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Laden Sie eigene Beispiel-Angebote hoch – MAESTRO lernt daraus, wie Sie Angebote aufbauen und formulieren, nicht was
          darin steht. Die KI kopiert keine Referenz: Gäste, Termin und Budget bestimmen später, was auf den Tisch kommt.
        </p>
      </div>

      {open == null ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {(Object.keys(PILLAR_META) as Pillar[]).map((p) => (
            <Card
              key={p}
              type="button"
              onClick={() => setOpen(p)}
              as="button" className="group flex items-start gap-3 p-5 text-left transition-shadow hover:shadow-md"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
                <Icon name={PILLAR_META[p].icon} className="text-[22px]" />
              </span>
              <div className="min-w-0">
                <p className="font-body-lg text-body-lg font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                  {PILLAR_META[p].label}
                </p>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{PILLAR_META[p].hint}</p>
                <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {docsQ.loading ? "…" : `${countFor(p)} Dokument${countFor(p) === 1 ? "" : "e"}`}
                </p>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <ReferenceDocsDetail pillar={open} onBack={() => setOpen(null)} docs={docs.filter((d) => d.pillar === open)} reload={docsQ.reload} />
      )}
    </div>
  );
}

function ReferenceDocsDetail({
  pillar,
  onBack,
  docs,
  reload,
}: {
  pillar: Pillar;
  onBack: () => void;
  docs: ReferenceDoc[];
  reload: () => void;
}) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractingId, setExtractingId] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(0);
  /** Zaehler, der die Stilprofil-Karte nach abgeschlossener Analyse zum Neubau anstoesst. */
  const [analysisDone, setAnalysisDone] = useState(0);
  // P4.2: Nach einem Upload arbeitet der Server weiter, auch wenn dieser Tab zugeht. Der zentrale
  // Anbieter ruht standardmaessig - dieser Anstoss weckt ihn, damit der Fortschritt ueberall
  // sichtbar wird (Topbar, Builder), ohne dass diese Seite eine eigene Abfrage baut.
  const { notifyStarted } = useReferenceProgress();

  /**
   * Analysiert die uebergebenen Dokumente nacheinander. Nacheinander statt parallel, weil ein
   * Sammel-Export intern schon mehrere Modellaufrufe parallel fuehrt - gleichzeitig mehrere
   * Dokumente zu starten wuerde das vervielfachen.
   */
  async function runAnalysis(targets: { id: string }[]): Promise<void> {
    if (targets.length === 0) return;
    setAnalyzing(targets.length);
    let ok = 0;
    let failed = 0;
    for (const t of targets) {
      try {
        await apiMutate(`/api/reference-docs/${t.id}/extract`, "POST");
        ok++;
      } catch {
        // Der genaue Grund steht bereits am betroffenen Dokument (failReason) - ihn hier zu
        // wiederholen brachte nur eine zweite, oft leere Fehlerzeile.
        failed++;
      } finally {
        setAnalyzing((n) => Math.max(0, n - 1));
        reload();
      }
    }
    if (failed > 0) {
      setError(
        failed === targets.length
          ? "Die Analyse ist fehlgeschlagen — der Grund steht beim Dokument. Über „Erneut analysieren“ nochmal versuchen."
          : `${failed} von ${targets.length} Dokumenten konnten nicht analysiert werden — der Grund steht jeweils beim Dokument.`,
      );
    }
    // Den Entwurf NUR anstossen, wenn wirklich etwas Analysiertes dazugekommen ist. Sonst
    // scheitert der Neubau zwangslaeufig ("Kein analysiertes Referenz-Dokument") und der
    // Betreiber sieht ZWEI Fehlermeldungen fuer EIN Problem.
    if (ok > 0) setAnalysisDone((n) => n + 1);
  }

  // Abgewiesene Dateien werden benannt statt still verworfen: wer eine .xlsx/.docx mit zwei PDFs
  // zusammen hineinzieht, sah bisher nur die PDFs in der Auswahl und keinen Hinweis, dass eine
  // Datei fehlt. Gleiches gilt fuer das Abschneiden jenseits von MAX_FILES.
  function acceptFiles(list: FileList | null) {
    const all = Array.from(list ?? []);
    const isSupported = (f: File) =>
      f.type === "application/pdf" ||
      f.type.startsWith("image/") ||
      TABLE_TYPES.includes(f.type) ||
      /\.(xlsx|csv)$/i.test(f.name); // manche Browser liefern gar keinen MIME-Typ
    const typed = all.filter(isSupported);
    const rejected = all.filter((f) => !isSupported(f));
    if (typed.length === 0) {
      setError("Nur PDF, JPEG, PNG, WebP, XLSX oder CSV.");
      return;
    }
    const hints: string[] = [];
    if (rejected.length > 0) {
      hints.push(`Übersprungen (nur PDF, JPEG, PNG, WebP, XLSX oder CSV): ${rejected.map((f) => f.name).join(", ")}.`);
    }
    if (typed.length > MAX_FILES) {
      hints.push(`Nur die ersten ${MAX_FILES} Dateien wurden übernommen.`);
    }
    setError(hints.length > 0 ? hints.join(" ") : null);
    const kept = typed.slice(0, MAX_FILES);
    setFiles(kept);
    // Titel aus dem Dateinamen vorbelegen: der Name ist da, danach zu fragen ist reine Reibung.
    // Nur wenn der Betreiber noch nichts getippt hat - eine Eingabe wird nie ueberschrieben.
    if (!title.trim() && kept[0]) setTitle(titleFromFileName(kept[0].name));
  }

  async function upload() {
    if (!title.trim() || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const encoded = await Promise.all(files.map(fileToBase64));
      const created = await apiMutate<ReferenceDoc[]>("/api/reference-docs", "POST", {
        pillar,
        title: title.trim(),
        note: note.trim() || undefined,
        files: encoded,
      });
      setTitle("");
      setNote("");
      setFiles([]);
      reload();
      notifyStarted();
      // Analyse laeuft von selbst: wer eine Referenz hochlaedt, will sie auch nutzen - der
      // frueher noetige Extra-Klick fragte nur nach dem, was der Upload schon gesagt hat.
      // Fehler hier duerfen den Upload nicht entwerten (die Datei liegt bereits sicher in R2),
      // deshalb nur eine Meldung; ueber "Erneut analysieren" bleibt der manuelle Weg offen.
      await runAnalysis(Array.isArray(created) ? created : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // "Neu analysieren" (Konzept §5.2): startet die Extraktion (P1.2) fuer EIN Dokument - auch
  // fuer status='failed' erneut aufrufbar. Ohne diesen Trigger bliebe jedes hochgeladene
  // Dokument dauerhaft 'uploaded' und der Stilprofil-Bau haette nie ein 'extracted'-Dokument.
  async function extractDoc(doc: ReferenceDoc) {
    setExtractingId(doc.id);
    setError(null);
    try {
      await apiMutate(`/api/reference-docs/${doc.id}/extract`, "POST");
      reload();
      // Auch der manuelle Weg stoesst den Profilbau an - sonst waere „Erneut analysieren"
      // stiller als der automatische Pfad und der Betreiber muesste doch wieder nachklicken.
      setAnalysisDone((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExtractingId(null);
    }
  }

  async function archive(doc: ReferenceDoc) {
    const ok = await bestaetigen({
      titel: `„${doc.title}“ archivieren?`,
      text: "Das Original bleibt erhalten, verschwindet aber aus dieser Liste.",
      bestaetigen: "Archivieren",
    });
    if (!ok) return;
    try {
      await apiMutate(`/api/reference-docs/${doc.id}`, "DELETE");
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-5">
      {bestaetigungsDialog}
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 font-body-sm text-body-sm text-secondary hover:text-primary dark:text-secondary-fixed-dim">
        <Icon name="arrow_back" className="text-[16px]" /> Zurück zu den Referenzen
      </button>
      <h3 className="font-title-sm text-title-sm font-semibold text-on-background dark:text-inverse-on-surface">{PILLAR_META[pillar].label}</h3>

      {error && <ErrorNote message={error} />}

      <Card
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          acceptFiles(e.dataTransfer.files);
        }}
        className={`flex flex-col gap-3 border-dashed p-5 transition-colors ${dragActive ? "border-primary bg-primary/5" : ""}`}
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Titel</label>
            <Input placeholder="z. B. Buffet Firmenfeier 2025" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="min-w-[220px] flex-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Notiz (optional)</label>
            <Input placeholder="z. B. 80 Gäste, Sommer" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/60 p-6 text-center hover:bg-surface-container-high dark:border-outline/40">
          <Icon name="upload_file" className="text-[26px] text-on-surface-variant" />
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {files.length > 0 ? `${files.length} Datei${files.length === 1 ? "" : "en"} ausgewählt` : `PDF, Foto oder Tabelle (XLSX/CSV) hierher ziehen oder klicken (max. ${MAX_FILES} Dateien, 8 MB je Datei)`}
          </span>
          <input type="file" accept="application/pdf,image/*,.xlsx,.csv,text/csv" multiple className="hidden" onChange={(e) => acceptFiles(e.target.files)} />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Nach dem Hochladen wird automatisch analysiert und daraus Ihre Hausschrift entworfen. Aktiv wird
            ein neuer Entwurf erst, wenn Inhaber ihn unten freigeben — bis dahin gilt die bisherige.
          </p>
          <Button type="button" disabled={busy || !title.trim() || files.length === 0} onClick={() => void upload()}>
            {busy ? (analyzing > 0 ? `Analysiert … (${analyzing})` : "Lädt hoch …") : "Hochladen & analysieren"}
          </Button>
        </div>
      </Card>

      {docs.length === 0 ? (
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Noch keine Referenz-Dokumente in dieser Säule.</p>
      ) : (
        <div className="divide-y divide-outline-variant/30 rounded-xl border border-outline-variant/30 dark:divide-outline/30 dark:border-outline/30">
          {docs.map((doc) => (
            <div key={doc.id} className="flex flex-wrap items-center gap-3 p-4">
              <Icon name={mediaIcon(doc.mediaType)} className="text-[22px] text-on-surface-variant" />
              <div className="min-w-[180px] flex-1">
                <p className="font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface">{doc.title}</p>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {formatBytes(doc.byteSize)}
                  {offerLabel(doc) ? ` · ${offerLabel(doc)}` : ""}
                  {doc.note ? ` · ${doc.note}` : ""}
                </p>
                {doc.status === "failed" && doc.failReason && <p className="font-body-sm text-body-sm text-error">{doc.failReason}</p>}
              </div>
              <StatusPill meta={STATUS_META[doc.status]} />
              {(doc.status === "uploaded" || doc.status === "failed") && (
                <Button type="button" variant="secondary" disabled={extractingId === doc.id} onClick={() => void extractDoc(doc)}>
                  <Icon name="auto_awesome" className="text-[16px]" /> {extractingId === doc.id ? "Analysiert …" : doc.status === "failed" ? "Erneut analysieren" : "Analysieren"}
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={() => void openAuthenticatedFile(`/api/reference-docs/${doc.id}/file`)}>
                <Icon name="open_in_new" className="text-[16px]" /> Original öffnen
              </Button>
              <Button type="button" variant="ghost" className="!px-2" title="Archivieren" onClick={() => void archive(doc)}>
                <Icon name="archive" className="text-[18px]" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <StyleProfileCard pillar={pillar} analysisDone={analysisDone} />
    </div>
  );
}

/** Leeres Profil-Geruest fuer neue Entwuerfe (Rebuild liefert immer einen vollstaendigen
 * StyleProfile - dies ist nur der Ausgangspunkt, bevor ein Entwurf existiert). */
function emptyProfile(pillar: Pillar): StyleProfile {
  return {
    v: 1,
    pillar,
    structure: { typicalCourses: [], itemCountRange: [0, 0], portionLogic: "", groupingStyle: "" },
    voice: { address: "neutral", descriptionPattern: "", vocabulary: [], avoid: [] },
    commercial: { pricePerGuestRangeCents: null, typicalAddOns: [] },
    seasonality: "",
    snippets: [],
    notes: "",
  };
}

const ADDRESS_LABEL: Record<StyleProfile["voice"]["address"], string> = { du: "Duzen", Sie: "Siezen", neutral: "Neutral" };

/**
 * Stilprofil-Karte (P1.6/P2.4, Konzept §5.4): lesbares, editierbares Formular, plus
 * „Aktivieren"/„Deaktivieren".
 *
 * SEIT 25.09.2026 wieder: neu gebaute Profile bleiben Entwurf bis zur Freigabe (Inhaber).
 * Vom 30.07. bis 25.09.2026 wurde ein neu gebautes Profil SOFORT aktiv (sql/160) - die
 * Kette laeuft vollautomatisch. Es liegt also in der Regel KEIN Entwurf mehr herum. Deshalb
 * arbeitet die Karte auf `draft ?? active`: sonst stuende hier "Noch kein Entwurf", obwohl ein
 * fertiges Profil aktiv ist, und das Bearbeiten-Formular waere unerreichbar.
 * `commercial.pricePerGuestRangeCents` (Entscheidung 3) wird bewusst NICHT
 * gerendert - bleibt im lokalen State erhalten (Round-Trip beim Speichern), wirkt nur im
 * Erzeugungs-Prompt.
 */
function StyleProfileCard({ pillar, analysisDone }: { pillar: Pillar; analysisDone: number }) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const profileQ = useApi<{ active: StyleProfileRow | null; draft: StyleProfileRow | null }>(`/api/style-profiles/${pillar}`);
  const draft = profileQ.data?.draft ?? null;
  const active = profileQ.data?.active ?? null;
  /** Das Profil, das die Karte anzeigt und bearbeitet: der Entwurf, sonst das aktive. */
  const bearbeitbar = draft ?? active;
  const [edit, setEdit] = useState<StyleProfile | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Es gibt neue Analysen, der Entwurf wurde aber von Hand bearbeitet - nicht ueberschreiben. */
  const [staleAfterEdit, setStaleAfterEdit] = useState(false);

  async function activate() {
    const ok = await bestaetigen({
      titel: `Entwurf für „${PILLAR_META[pillar].label}“ aktivieren?`,
      text: "Ab sofort beeinflusst diese Hausschrift JEDEN neu erzeugten Angebotsvorschlag dieser Säule (Aufbau, Benennung, Tonfall — nie die Speisen selbst). Ein vorhandenes aktives Profil wird dabei archiviert.",
      bestaetigen: "Aktivieren",
    });
    if (!ok) return;
    setActivating(true);
    setError(null);
    try {
      await apiMutate(`/api/style-profiles/${pillar}/activate`, "POST");
      profileQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActivating(false);
    }
  }

  async function deactivate() {
    const ok = await bestaetigen({
      titel: `Aktives Profil für „${PILLAR_META[pillar].label}“ deaktivieren?`,
      text: "Die Angebotserzeugung läuft danach wieder ohne Hausschrift-Einfluss, exakt wie ohne Profil.",
      bestaetigen: "Deaktivieren",
    });
    if (!ok) return;
    setActivating(true);
    setError(null);
    try {
      await apiMutate(`/api/style-profiles/${pillar}/deactivate`, "POST");
      profileQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setActivating(false);
    }
  }

  // Entwurf neu geladen (z.B. nach Rebuild) -> lokalen Bearbeitungsstand neu aus dem Server-Stand
  // aufsetzen. Absichtlich an draft?.id gekoppelt (nicht an draft selbst), damit ein laufender
  // manueller Edit nicht bei jedem Re-Render ueberschrieben wird.
  useEffect(() => {
    setEdit(bearbeitbar?.profile ?? null);
  }, [bearbeitbar?.id]);

  async function rebuild() {
    setRebuilding(true);
    setError(null);
    try {
      await apiMutate(`/api/style-profiles/${pillar}/rebuild`, "POST");
      setStaleAfterEdit(false);
      profileQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRebuilding(false);
    }
  }

  /**
   * Automatischer Neubau nach einer Analyse - aber NIEMALS ueber eine Handkorrektur hinweg.
   *
   * Woran das erkennbar ist, ohne ein zusaetzliches Feld: „Neu erstellen" legt serverseitig eine
   * NEUE Zeile an (createdAt == updatedAt), eine gespeicherte Korrektur aktualisiert die
   * bestehende (updatedAt ruecken vor). Ist der Entwurf also von Hand bearbeitet, wird hier
   * nicht gebaut, sondern nur ein Hinweis gesetzt - der Betreiber entscheidet selbst.
   */
  useEffect(() => {
    if (analysisDone === 0 || rebuilding || profileQ.loading) return;
    // Auf `bearbeitbar` geprueft, nicht nur auf den Entwurf: Seit dem Scharfschalten ist die von
    // Hand korrigierte Zeile meist die AKTIVE - ohne diesen Blick wuerde der Browser sie beim
    // naechsten Analyse-Ende ueberschreiben, genau gegen die Zusage aus P3.2.
    if (bearbeitbar && wasHandEdited(bearbeitbar)) {
      setStaleAfterEdit(true);
      return;
    }
    void rebuild();
    // Absichtlich nur an `analysisDone` gekoppelt: der Neubau soll genau einmal je
    // abgeschlossener Analyse laufen, nicht bei jedem Rerender der Karte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisDone]);

  async function save() {
    if (!edit) return;
    setSaving(true);
    setError(null);
    try {
      await apiMutate(`/api/style-profiles/${pillar}/draft`, "PUT", { profile: edit });
      profileQ.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const current = edit ?? (bearbeitbar ? bearbeitbar.profile : emptyProfile(pillar));
  const linesToArray = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);
  const csvToArray = (s: string) => s.split(",").map((l) => l.trim()).filter(Boolean);

  return (
    <Card className="space-y-4 p-5">
      {bestaetigungsDialog}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">Stilprofil</h4>
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {active ? `Aktiv: Version ${active.version}` : "Noch kein aktives Profil"}
            {draft ? ` · Entwurf: Version ${draft.version}` : ""}
            {active?.approvedBy === "automatik" ? " · automatisch aktiviert" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" disabled={rebuilding} onClick={() => void rebuild()}>
            <Icon name="auto_fix_high" className="text-[16px]" /> {rebuilding ? "Erstellt …" : "Aus Dokumenten neu erstellen"}
          </Button>
          {draft && (
            <Button type="button" variant="secondary" disabled={activating} onClick={() => void activate()}>
              <Icon name="check_circle" className="text-[16px]" /> {activating ? "Gibt frei …" : "Freigeben"}
            </Button>
          )}
          {active && (
            <Button type="button" variant="ghost" disabled={activating} onClick={() => void deactivate()}>
              <Icon name="cancel" className="text-[16px]" /> {activating ? "Deaktiviert …" : "Deaktivieren"}
            </Button>
          )}
        </div>
      </div>

      {error && <ErrorNote message={error} />}

      {staleAfterEdit && (
        <p className="rounded-lg bg-primary/8 p-3 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          Es wurden neue Dokumente analysiert. Ihre eigenen Änderungen an diesem Profil bleiben erhalten –
          „Aus Dokumenten neu erstellen“ verwirft sie und baut es frisch auf.
        </p>
      )}

      {!bearbeitbar ? (
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Noch kein Profil. Laden Sie oben Referenz-Angebote dieser Säule hoch – Analyse, Profil und Aktivierung laufen dann von selbst.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Gangfolge (eine Zeile je Gang)</label>
            <Textarea
              className="min-h-[80px]"
              value={current.structure.typicalCourses.join("\n")}
              onChange={(e) => setEdit({ ...current, structure: { ...current.structure, typicalCourses: linesToArray(e.target.value) } })}
            />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Übliche Positionsanzahl (von–bis)</label>
            {/* min-w-0 direkt am Input: kein Label-Wrapper hier — s. posteingang.tsx fuer
                dasselbe Muster. */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                className="min-w-0"
                value={current.structure.itemCountRange[0]}
                onChange={(e) => setEdit({ ...current, structure: { ...current.structure, itemCountRange: [Number(e.target.value) || 0, current.structure.itemCountRange[1]] } })}
              />
              <span className="text-secondary dark:text-secondary-fixed-dim">–</span>
              <Input
                type="number"
                className="min-w-0"
                value={current.structure.itemCountRange[1]}
                onChange={(e) => setEdit({ ...current, structure: { ...current.structure, itemCountRange: [current.structure.itemCountRange[0], Number(e.target.value) || 0] } })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Portionslogik</label>
            <Input value={current.structure.portionLogic} onChange={(e) => setEdit({ ...current, structure: { ...current.structure, portionLogic: e.target.value } })} />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Benennung der Blöcke</label>
            <Input value={current.structure.groupingStyle} onChange={(e) => setEdit({ ...current, structure: { ...current.structure, groupingStyle: e.target.value } })} />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Anrede</label>
            <Select
              value={current.voice.address}
              onValueChange={(v) => setEdit({ ...current, voice: { ...current.voice, address: v as StyleProfile["voice"]["address"] } })}
              options={(Object.keys(ADDRESS_LABEL) as StyleProfile["voice"]["address"][]).map((a) => ({ value: a, label: ADDRESS_LABEL[a] }))}
            />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Beschreibungsmuster</label>
            <Input value={current.voice.descriptionPattern} onChange={(e) => setEdit({ ...current, voice: { ...current.voice, descriptionPattern: e.target.value } })} />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Typische Wendungen (kommagetrennt)</label>
            <Input value={current.voice.vocabulary.join(", ")} onChange={(e) => setEdit({ ...current, voice: { ...current.voice, vocabulary: csvToArray(e.target.value) } })} />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Vermeidet (kommagetrennt)</label>
            <Input value={current.voice.avoid.join(", ")} onChange={(e) => setEdit({ ...current, voice: { ...current.voice, avoid: csvToArray(e.target.value) } })} />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Übliche Zusatzposten (kommagetrennt)</label>
            <Input
              value={current.commercial.typicalAddOns.join(", ")}
              onChange={(e) => setEdit({ ...current, commercial: { ...current.commercial, typicalAddOns: csvToArray(e.target.value) } })}
            />
          </div>
          <div className="space-y-1">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Saisonalität dieses Betriebs</label>
            <Input value={current.seasonality} onChange={(e) => setEdit({ ...current, seasonality: e.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Formmuster (eine Zeile je Beispiel: „Bezeichnung | Beschreibung")
            </label>
            <Textarea
              className="min-h-[60px]"
              value={current.snippets.map((s) => `${s.label} | ${s.text}`).join("\n")}
              onChange={(e) =>
                setEdit({
                  ...current,
                  snippets: linesToArray(e.target.value).map((line) => {
                    const [label, ...rest] = line.split("|");
                    return { label: (label ?? "").trim(), text: rest.join("|").trim() };
                  }),
                })
              }
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Notizen</label>
            <Textarea className="min-h-[60px]" value={current.notes} onChange={(e) => setEdit({ ...current, notes: e.target.value })} />
          </div>
          <div className="flex justify-end sm:col-span-2">
            <Button type="button" disabled={saving} onClick={() => void save()}>
              {saving ? "Speichert …" : "Speichern"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
